import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ROUTES } from "../config/routes";
import { PromotionGroupsAdminService } from "../api/generated/services/PromotionGroupsAdminService";
import { PromotionGroupViewModel } from "../api/generated/models/PromotionGroupViewModel";
import { PromotionGroupType } from "../api/generated/models/PromotionGroupType";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import {
  fetchBusinessesByIds,
  TypesenseBusinessDocument,
} from "../utils/typesenseBusinessSearch";
import "../api/openApiConfig";

const isFeaturedGroup = (group: PromotionGroupViewModel) =>
  group.groupType === PromotionGroupType.FEATURED;

interface SortableGroupCardProps {
  group: PromotionGroupViewModel;
  businessMap: Map<string, TypesenseBusinessDocument>;
  onClick: (group: PromotionGroupViewModel) => void;
}

const SortableGroupCard: React.FC<SortableGroupCardProps> = ({
  group,
  businessMap,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id!, disabled: isFeaturedGroup(group) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const businesses = (group.businessIds || [])
    .map((id) => businessMap.get(id))
    .filter((b): b is TypesenseBusinessDocument => b !== undefined);

  const typeBadgeColor =
    group.groupType === PromotionGroupType.FEATURED
      ? "bg-purple-100 text-purple-700"
      : "bg-gray-100 text-gray-700";
  const featured = isFeaturedGroup(group);

  const leftBorder = group.isPublished ? "border-l-green-500" : "border-l-gray-300";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg p-4 mb-3 shadow-sm border border-gray-200 border-l-4 ${leftBorder} cursor-pointer hover:shadow-md transition-shadow`}
      onClick={() => onClick(group)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Drag handle — hidden for FEATURED group (pinned to top) */}
          <div
            {...(featured ? {} : attributes)}
            {...(featured ? {} : listeners)}
            className={`mt-1 flex-shrink-0 ${
              featured
                ? "text-gray-200 cursor-default"
                : "cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900">
                {group.title || "Untitled"}
              </h3>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  group.isPublished
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {group.isPublished ? "Active" : "Draft"}
              </span>
            </div>
            {group.subtitle && (
              <p className="text-sm text-gray-500 mt-0.5">{group.subtitle}</p>
            )}
            {(group.visibilityStartAt || group.visibilityEndAt) && (
              <p className="text-xs text-gray-400 mt-1">
                {group.visibilityStartAt &&
                  `From: ${new Date(
                    group.visibilityStartAt as any
                  ).toLocaleDateString()}`}
                {group.visibilityStartAt && group.visibilityEndAt && " · "}
                {group.visibilityEndAt &&
                  `Until: ${new Date(
                    group.visibilityEndAt as any
                  ).toLocaleDateString()}`}
              </p>
            )}
          </div>
        </div>

        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2 ${typeBadgeColor}`}
        >
          {group.groupType}
        </span>
      </div>

      {/* Business carousel */}
      {businesses.length > 0 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
          {businesses.map((biz) => {
            const logoUrl = biz.logo_url?.startsWith("//")
              ? `https:${biz.logo_url}`
              : biz.logo_url;
            return (
              <div
                key={biz.id}
                className="flex-shrink-0 flex flex-col items-center w-16"
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={biz.business_name}
                    className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-[8px]">No logo</span>
                  </div>
                )}
                <span className="text-[10px] text-gray-600 mt-1 text-center leading-tight line-clamp-2">
                  {biz.business_name}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {businesses.length === 0 && (group.businessIds?.length || 0) === 0 && (
        <div className="mt-3 flex gap-3 pb-2">
          <div className="flex-shrink-0 flex flex-col items-center w-16">
            <div className="w-12 h-12 rounded-lg bg-gray-100 border border-dashed border-gray-300" />
            <div className="mt-1 w-10 h-2 rounded bg-gray-100" />
          </div>
        </div>
      )}
    </div>
  );
};

const AdminFeaturedPartners: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<PromotionGroupViewModel[]>([]);
  const [businessMap, setBusinessMap] = useState<
    Map<string, TypesenseBusinessDocument>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadGroups = useCallback(async () => {
    try {
      await PromotionGroupsAdminService.postApiPromotionGroupsAdminEnsureSpecialGroups();
      const data = await PromotionGroupsAdminService.getApiPromotionGroupsAdmin();
      setGroups(data);

      // Collect all business IDs and fetch from Typesense
      const allIds = Array.from(
        new Set(data.flatMap((g) => g.businessIds || []))
      );
      if (allIds.length > 0) {
        const docs = await fetchBusinessesByIds(allIds);
        const map = new Map<string, TypesenseBusinessDocument>();
        for (const doc of docs) {
          map.set(doc.id, doc);
        }
        setBusinessMap(map);
      }
    } catch (error) {
      console.error("Error loading promotion groups:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Don't allow dropping onto the FEATURED group slot
    const overGroup = groups.find((g) => g.id === over.id);
    if (overGroup && isFeaturedGroup(overGroup)) return;

    const sortable = groups.filter((g) => !isFeaturedGroup(g));
    const oldIndex = sortable.findIndex((g) => g.id === active.id);
    const newIndex = sortable.findIndex((g) => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedSortable = arrayMove(sortable, oldIndex, newIndex);
    const featuredGroup = groups.find((g) => isFeaturedGroup(g));
    const reordered = featuredGroup
      ? [featuredGroup, ...reorderedSortable]
      : reorderedSortable;
    setGroups(reordered);

    try {
      await PromotionGroupsAdminService.putApiPromotionGroupsAdminReorder({
        orderedIds: reordered.map((g) => g.id!),
      });
    } catch (error) {
      console.error("Error reordering groups:", error);
      // Revert on failure
      setGroups(groups);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const newGroup =
        await PromotionGroupsAdminService.postApiPromotionGroupsAdmin({
          title: "New Group",
          isPublished: false,
        });
      navigate(ROUTES.ADMIN_FEATURED_PARTNERS_EDIT(newGroup.id));
    } catch (error) {
      console.error("Error creating group:", error);
    } finally {
      setCreating(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h2 className="font-medium">Featured Partners</h2>
          <p className="text-sm text-gray-600 mt-2">
            Manage promotion groups displayed as carousels in the mobile app
          </p>
        </div>
        <Button onClick={handleCreate} isLoading={creating}>
          Add New Group
        </Button>
      </div>

      {/* FEATURED group is always first and not sortable */}
      {groups.filter((g) => isFeaturedGroup(g)).map((group) => (
        <SortableGroupCard
          key={group.id}
          group={group}
          businessMap={businessMap}

          onClick={(g) =>
            navigate(ROUTES.ADMIN_FEATURED_PARTNERS_EDIT(g.id))
          }
        />
      ))}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groups.filter((g) => !isFeaturedGroup(g)).map((g) => g.id!)}
          strategy={verticalListSortingStrategy}
        >
          {groups.filter((g) => !isFeaturedGroup(g)).map((group) => (
            <SortableGroupCard
              key={group.id}
              group={group}
              businessMap={businessMap}
    
              onClick={(g) =>
                navigate(ROUTES.ADMIN_FEATURED_PARTNERS_EDIT(g.id))
              }
            />
          ))}
        </SortableContext>
      </DndContext>

      {groups.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No promotion groups yet. Click "Add New Group" to create one.
        </div>
      )}

    </div>
  );
};

export default AdminFeaturedPartners;
